import React, { FC, useState, useEffect } from 'react';
import { Button, Modal } from 'antd';
import { getLangKey } from 'models/Language';
import { View } from '../project_nav/types';
import Step from './Step';
import * as translations from './strings';
import styles from './styles.module.css';
import { trackPage } from 'appJS/utils/analytics';
import gettingStartedThumbnail from './getting-started-thumbnail.png';
import { Play, PlayCircle } from 'lucide-react';
import { Support } from './Support';
import AddWebsiteModal from '../../../../clients/design_assets/Sites/AddWebsiteModal';
import { VideoModal } from './VideoModal';

const strings = translations[getLangKey()];

type Props = {
  project: any;
  setView: (view: View | null) => void;
  gettingStartedCheck: {
    hasSeenOnboardingVideo: boolean;
    hasDeliverables: boolean;
    hasManagedFeedback: boolean;
    hasInvitedUsers: boolean;
    hasIntegrations: boolean;
  };
  canAccessBilling: boolean;
  handleChecklistComplete: () => void;
  showAssetsPage: () => void;
  container: HTMLDivElement;
  kanbanCount: number;
  currentUserId: string;
};

const GettingStartedChecklist: FC<Props> = ({
  project,
  setView,
  gettingStartedCheck,
  canAccessBilling,
  handleChecklistComplete,
  showAssetsPage,
  container,
  currentUserId
}) => {
  const [isGettingStartedComplete, setIsGettingStartedComplete] = useState(
    false
  );
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState<boolean>(false);

  const showVideoModal = () => {
    setIsVideoModalOpen(true);
    localStorage.setItem('checklist_onboarding_video_seen', 'true');
  };

  const mandatoryCompletedStepsCount = () => {
    const filteredGettingStartedCheck = Object.fromEntries(
      Object.entries(gettingStartedCheck).filter(
        ([key]) => key !== 'hasIntegrations'
      )
    );
    return Object.values(filteredGettingStartedCheck).filter(Boolean).length;
  };

  useEffect(() => {
    trackPage('View Setup guide page', {
      project_id: project?.id
    });
  }, []);

  useEffect(() => {
    if (isGettingStartedComplete) {
      handleChecklistComplete();
    }
  }, [isGettingStartedComplete]);

  useEffect(() => {
    const completedStepsCount = Object.values(gettingStartedCheck).filter(
      Boolean
    ).length;
    const totalStepsCount = Object.values(gettingStartedCheck).length;

    if (completedStepsCount === totalStepsCount) {
      setIsGettingStartedComplete(true);
    }
  }, [gettingStartedCheck]);

  const handleClickUsersPage = event => {
    event.preventDefault();
    event.stopPropagation();
    window.share_project_props.openModal('member');
  };

  useEffect(() => {
    // add outline to the first unchecked step/next step to action
    const uncheckedStepContainers: NodeListOf<HTMLDivElement> = document.querySelectorAll(
      `.${styles.stepContainer}:not(.${styles.checkedStep})`
    );

    if (uncheckedStepContainers.length > 0) {
      uncheckedStepContainers[0].style.outline =
        '2px solid var(--colorPrimary)';
    }
  });

  const navigateToKanban = () => {
    setView(View.Kanban);
  };

  const isTaskBoardTourFinished =
    project?.projectBoardOnboardingCompleted ||
    localStorage.getItem(`${currentUserId}_finished_project_onboarding`);

  const showAlert = () => {
    Modal.info({
      title: 'Add a website or file',
      footer: null,
      content: (
        <span>
          You first have to add a website or file.
          <br />
          <br />
          <Button
            type="primary"
            onClick={() => {
              showAssetsPage();
              Modal.destroyAll();
            }}
          >
            Add
          </Button>
        </span>
      ),
      maskClosable: true,
      closable: true,
      centered: true,
      wrapClassName: 'antd-container'
    });
  };

  const steps = [
    {
      key: 'intro',
      isChecked:
        gettingStartedCheck.hasSeenOnboardingVideo ||
        localStorage.getItem('checklist_onboarding_video_seen'),
      handleClick: showVideoModal,
      title: strings.quickIntroduction,
      description: (
        <div className={styles.quickIntroduction}>
          <p> {strings.quickIntroductionDescription}</p>
          <div className={styles.thumbnailContainer} onClick={showVideoModal}>
            <img
              src={gettingStartedThumbnail}
              className={styles.previewThumbnail}
              alt="getting_started_thumbnail"
            />
            <div className={styles.playCircle}>
              <div className={styles.innerCircle}>
                <Play className={styles.playIcon} />
              </div>
            </div>
          </div>
        </div>
      ),
      actionButtons: (
        <>
          <Button
            type="primary"
            icon={<PlayCircle size={20} />}
            onClick={showVideoModal}
          >
            {strings.watchNow}
          </Button>
        </>
      )
    },
    {
      key: 'addWebsite',
      isChecked: gettingStartedCheck.hasDeliverables,
      handleClick: showAssetsPage,
      title: strings.addADeliverableTitle,
      description: strings.addADeliverableDescription,
      actionButtons: (
        <Button type="primary" onClick={showAssetsPage}>
          {strings.add}
        </Button>
      ),
      elevioArticleId: 11467109
    },
    {
      key: 'manageFeedback',
      isChecked: isTaskBoardTourFinished,
      handleClick: () => navigateToKanban,
      title: strings.manageFeedback,
      description: strings.manageFeedbackDescription,
      actionButtons: (
        <>
          <Button
            type="primary"
            onClick={
              gettingStartedCheck.hasDeliverables ? navigateToKanban : showAlert
            }
          >
            {strings.giveFeedback}
          </Button>
        </>
      )
    },
    {
      key: 'inviteTeam',
      isChecked: gettingStartedCheck.hasInvitedUsers,
      handleClick: e => handleClickUsersPage(e),
      title: strings.inviteTeamTitle,
      description: strings.inviteTeamDescription,
      actionButtons: (
        <>
          <Button type="primary" onClick={handleClickUsersPage}>
            {strings.share}
          </Button>
        </>
      ),
      elevioArticleId: 11467170
    },
    {
      key: 'setupIntegration',
      isChecked: gettingStartedCheck.hasIntegrations,
      handleClick: () => setView(View.Integrations),
      title: strings.setupIntegrationTitle,
      description: strings.setupIntegrationDescription,
      actionButtons: (
        <>
          <Button type="primary" onClick={() => setView(View.Integrations)}>
            {strings.setup}
          </Button>
        </>
      ),
      elevioArticleId: 11467159,
      showSkip: mandatoryCompletedStepsCount() === 4
    }
  ];

  return (
    <div className={styles.container} data-hideLegacyHeader="true">
      <AddWebsiteModal
        isWebsiteModalOpen={isWebsiteModalOpen}
        activeItem="javascript"
        container={container}
        handleCloseWebsiteModal={() => setIsWebsiteModalOpen(false)}
        hideInstallInstructions
      />
      <VideoModal
        open={isVideoModalOpen}
        onCancel={() => setIsVideoModalOpen(false)}
      />
      <div className={styles.headerAndContentWrapper}>
        <h1>{strings.setupYourProject}</h1>
        <p>{strings.setupIntro}</p>
        <div className={styles.contentWrapper}>
          <div className={styles.stepsContainer}>
            {steps.map(step => {
              return (
                <Step
                  key={step.key}
                  isChecked={step.isChecked}
                  handleClick={step.handleClick}
                  title={step.title}
                  description={step.description}
                  actionButtons={step.actionButtons}
                  showSkip={step.showSkip || false}
                  elevioArticleId={step.elevioArticleId}
                  handleChecklistComplete={handleChecklistComplete}
                />
              );
            })}
          </div>
          <Support
            isVideoModalOpen={isVideoModalOpen}
            setIsVideoModalOpen={setIsVideoModalOpen}
            container={container}
            canAccessBilling={canAccessBilling}
            organizationId={project?.organizationId}
          />
        </div>
      </div>
    </div>
  );
};

export default GettingStartedChecklist;
